// tds.service.js
// TDS (Tax Deducted at Source) calculation service with backend integration
// Supports old vs new tax regime, exemptions, and tax slab-based calculation

import { api } from './api';

class TDSService {
    /**
     * Calculate TDS using backend API with automatic fallback
     * @param {string} employeeId - Employee UUID
     * @param {number} annualGross - Annual gross salary
     * @param {string} financialYear - Financial year in 'YYYY-YY' format (optional)
     * @returns {Promise<Object>} TDS calculation result with source tracking
     */
    async calculateTDS(employeeId, annualGross, financialYear = null) {
        try {
            // Get current month in YYYY-MM format if financialYear not provided
            const currentMonth = financialYear || (() => {
                const now = new Date();
                return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            })();

            // Prepare request payload to match backend expectations
            const payload = {
                employeeId,
                grossSalary: parseFloat(annualGross) || 0,  // Backend expects 'grossSalary'
                month: currentMonth  // Backend expects 'month' in YYYY-MM format
            };

            // Call backend TDS calculation API
            const response = await api.post('/api/tds/calculate', payload);

            if (response.data.success) {
                const data = response.data.data;

                return {
                    tdsAmount: data.tdsAmount || 0,
                    annualTDS: data.taxDetails?.total_annual_tax || 0,
                    tdsDetails: data.taxDetails || null,
                    taxRegime: data.taxDetails?.tax_regime || 'new',
                    exemptionApplied: data.exemptionApplied || false,
                    exemptionReason: data.exemptionReason || null,
                    source: 'backend'
                };
            }

            // Backend returned unsuccessful response
            throw new Error(response.data.message || 'Backend TDS calculation failed');

        } catch (error) {
            console.warn('Backend TDS calculation failed, using frontend fallback:', error.message);

            // Fallback to frontend calculation (simplified)
            // This is a very basic fallback - actual TDS calculation is complex
            const annualTaxable = parseFloat(annualGross || 0);
            let annualTax = 0;

            // Simplified new regime slabs for FY 2024-25
            if (annualTaxable <= 300000) {
                annualTax = 0;
            } else if (annualTaxable <= 600000) {
                annualTax = (annualTaxable - 300000) * 0.05;
            } else if (annualTaxable <= 900000) {
                annualTax = 15000 + (annualTaxable - 600000) * 0.10;
            } else if (annualTaxable <= 1200000) {
                annualTax = 45000 + (annualTaxable - 900000) * 0.15;
            } else if (annualTaxable <= 1500000) {
                annualTax = 90000 + (annualTaxable - 1200000) * 0.20;
            } else {
                annualTax = 150000 + (annualTaxable - 1500000) * 0.30;
            }

            // Apply 4% cess
            annualTax = annualTax * 1.04;

            // Rebate u/s 87A (if taxable income <= 7 lakhs)
            if (annualTaxable <= 700000 && annualTax > 0) {
                annualTax = Math.max(0, annualTax - 25000);
            }

            const monthlyTDS = Math.round(annualTax / 12);

            return {
                tdsAmount: monthlyTDS,
                annualTDS: Math.round(annualTax),
                tdsDetails: {
                    annual_taxable_income: annualTaxable,
                    annual_tax: Math.round(annualTax),
                    monthly_tds: monthlyTDS,
                    tax_regime: 'new',
                    note: 'Fallback calculation - may not include all exemptions'
                },
                taxRegime: 'new',
                exemptionApplied: false,
                exemptionReason: null,
                source: 'frontend-fallback'
            };
        }
    }

    /**
     * Synchronous TDS calculation (simplified, for proration/projection use)
     * @param {number} annualGross - Annual gross salary
     * @returns {number} Monthly TDS amount
     */
    calculateTDSSync(annualGross) {
        const annualTaxable = parseFloat(annualGross || 0);
        let annualTax = 0;

        // Simplified new regime slabs for FY 2024-25
        if (annualTaxable <= 300000) {
            annualTax = 0;
        } else if (annualTaxable <= 600000) {
            annualTax = (annualTaxable - 300000) * 0.05;
        } else if (annualTaxable <= 900000) {
            annualTax = 15000 + (annualTaxable - 600000) * 0.10;
        } else if (annualTaxable <= 1200000) {
            annualTax = 45000 + (annualTaxable - 900000) * 0.15;
        } else if (annualTaxable <= 1500000) {
            annualTax = 90000 + (annualTaxable - 1200000) * 0.20;
        } else {
            annualTax = 150000 + (annualTaxable - 1500000) * 0.30;
        }

        // Apply 4% cess
        annualTax = annualTax * 1.04;

        // Rebate u/s 87A (if taxable income <= 7 lakhs)
        if (annualTaxable <= 700000 && annualTax > 0) {
            annualTax = Math.max(0, annualTax - 25000);
        }

        return Math.round(annualTax / 12);
    }

    /**
     * Get current financial year
     * @returns {string} Financial year (e.g., '2024-25')
     */
    getCurrentFinancialYear() {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1; // 0-indexed

        if (month >= 4) {
            // April onwards - current FY
            return `${year}-${String(year + 1).slice(-2)}`;
        } else {
            // Jan-March - previous FY
            return `${year - 1}-${String(year).slice(-2)}`;
        }
    }
}

// Export singleton instance
export default new TDSService();
